import { CopyIcon, DoneIcon } from "Components/Shared/Icons";
import {
  ClickAwayListener,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useIsMobile } from "Hooks/useIsMobile";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  text: string;
  accessibilityLabel: string;
  successMessage?: string;
  hoverMessage?: string;
  size?: number;
};

export const CopyToClipboard = ({
  text,
  accessibilityLabel,
  successMessage,
  hoverMessage,
  size = 24,
}: Props) => {
  const [isCopySuccess, setIsCopySuccess] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useResource();

  const resetCopySuccess = () => {
    setIsCopySuccess(false);
  };

  const handleCopyClick = async () => {
    await navigator.clipboard.writeText(text);
    setIsCopySuccess(true);
    if (isMobile) {
      // Not showing tooltip on mobile, instead snackbar is shown
      return;
    }
    setTimeout(() => resetCopySuccess(), 2000);
  };

  return (
    <>
      <ClickAwayListener onClickAway={resetCopySuccess}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip
            arrow
            placement="top"
            title={
              isCopySuccess
                ? successMessage
                : hoverMessage || t(Resources.Common.Copy)
            }
            slotProps={{ popper: { disablePortal: true } }}
          >
            <IconButton
              disableRipple
              onClick={handleCopyClick}
              aria-label={accessibilityLabel}
              sx={{ p: 0, ml: 1 }}
            >
              {isCopySuccess ? (
                <DoneIcon height={size} width={size} />
              ) : (
                <CopyIcon height={size} width={size} />
              )}
            </IconButton>
          </Tooltip>
        </div>
      </ClickAwayListener>
      {successMessage && isMobile && (
        <Snackbar
          open={isCopySuccess}
          autoHideDuration={2000}
          sx={{ mb: [15, 11, 0] }}
          message={successMessage}
          onClose={() => setIsCopySuccess(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      )}
    </>
  );
};
